<script setup lang="ts">
import { cloneDeep, debounce } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import ResourcePickerEquipment from './ResourcePickerEquipment.vue';
import ResourcePickerUser from './ResourcePickerUser.vue';
import { useAlert } from '/@elements/DfAlert/DfAlert.vue';
import { useProjectStore } from '/@features/project';
import { Project } from '/@features/project/project.types';
import { Modules, StatusGroups } from '/@types/ids';
import { ScheduleResource } from '/@types/resources';
import { ValidationRule, validateForm } from '/@utilities/form';
import { newListOpt } from '/@utilities/opt';

const columns = [
  { id: 'statusGroupId', filterKey: 'statusGroupId' },
  { id: 'typeId', filterKey: 'typeId' },
  { id: 'caseworkerId', filterKey: 'caseworkerId' },
];

const props = defineProps<{
  selectedResource: ScheduleResource;
  saving: boolean;
}>();

const emit = defineEmits(['create', 'close']);

const projectStore = useProjectStore();

const eventForm = ref(cloneDeep(props.selectedResource));

const newBookingSearchOpt = ref(
  newListOpt({
    filters: [
      { columnId: 'statusGroupId', values: [StatusGroups.Aktive, StatusGroups.Prosjektering] },
    ],
    sortBy: 'id',
    limit: 10,
  }),
);
const newBookingSearchLoading = ref(false);
const newBookingSearchProjects = ref<Project[]>([]);

watch(
  newBookingSearchOpt,
  debounce(() => {
    newBookingSearchLoading.value = true;
    projectStore
      .loadProjectPage({ moduleId: Modules.Ressurs, opt: newBookingSearchOpt.value, columns })
      .then((data) => {
        newBookingSearchProjects.value = data.entries;
      })
      .finally(() => {
        newBookingSearchLoading.value = false;
      });
  }, 500),
  { deep: true },
);

watch(
  () => eventForm.value.fromDate,
  (date) => {
    if (!date || isNaN(date.getTime())) {
      eventForm.value.fromDate = props.selectedResource.fromDate;
    }
  },
);

watch(
  () => eventForm.value.toDate,
  (date) => {
    if (!date || isNaN(date.getTime())) {
      eventForm.value.toDate = props.selectedResource.toDate;
    }
  },
);

const alert = useAlert();

const isGroup = computed(() => props.selectedResource.resourceType === 'group');
const isEquipment = computed(() => props.selectedResource.resourceType === 'equipment');

const selectedGuids = ref<Set<string>>(new Set());

const projectEntriesMap = computed(
  () => new Map(newBookingSearchProjects.value.map((p) => [p.id, p])),
);

function submitEvent(event: ScheduleResource) {
  const resourceLabel = isEquipment.value ? 'Utstyr' : 'Bruker';
  const validation: ValidationRule[] = [
    [!event.projectId, 'Prosjekt kreves'],
    [!event.fromDate || !event.toDate, 'Fra og til tid kreves'],
    [event.fromDate > event.toDate, 'Fra tid må være før til tid'],
    [isGroup.value ? false : selectedGuids.value.size === 0, `${resourceLabel} kreves`],
  ];

  const { ok, message } = validateForm(validation);

  if (ok) {
    if (selectedGuids.value.size > 0) {
      event.additionalUsers = [...selectedGuids.value];
    }
    emit('create', event);
  } else {
    alert.write(String(message));
  }
}

projectStore
  .loadProjectPage({ moduleId: Modules.Ressurs, opt: newBookingSearchOpt.value, columns })
  .then((data) => {
    newBookingSearchProjects.value = data.entries;
  })
  .catch(() => {
    alert.write('Kunne ikke laste prosjekter');
  })
  .finally(() => {
    newBookingSearchLoading.value = false;
  });
</script>

<template>
  <form class="dialog" v-on:submit.prevent="submitEvent(eventForm)">
    <header class="head">
      <df-button v-on:click="emit('close')">
        <template v-slot:icon>
          <df-icon code="f060" />
        </template>
      </df-button>

      <h1 class="title-md">Ny avtale</h1>
    </header>

    <df-alert v-model="alert" />

    <div class="dl card" style="padding: 0">
      <label class="dl__group dl__group--large dl__group--actions ghost-label">
        <h2 class="dl__topic">Prosjekt</h2>

        <df-select3
          v-model="eventForm.projectId"
          v-model:search="newBookingSearchOpt.search"
          remote-search
          :remote-loading="newBookingSearchLoading"
          :entries="projectEntriesMap"
          label="Prosjekter"
          display="name"
        >
          <template v-slot:trigger="{ toggle, value }">
            <button class="ghost-input" type="button" v-on:click="toggle()">
              {{ value }}
            </button>
          </template>
        </df-select3>

        <div class="dl__actions">
          <df-icon code="f107" style="align-self: center" />
        </div>
      </label>

      <resource-picker-equipment
        v-if="!isGroup && isEquipment"
        v-model="selectedGuids"
        :initial-guid="selectedResource.resourceGuid"
      />
      <resource-picker-user
        v-else-if="!isGroup"
        v-model="selectedGuids"
        :initial-guid="selectedResource.resourceGuid"
      />

      <label class="dl__group ghost-label">
        <h2 class="dl__topic">Fra</h2>
        <input
          class="dl__description ghost-input"
          type="datetime-local"
          v-on:input="
            eventForm.fromDate = new Date(
              $event.target.valueAsNumber + eventForm.fromDate.getTimezoneOffset() * 1000 * 60,
            )
          "
          :valueAsNumber="
            eventForm.fromDate.getTime() - eventForm.fromDate.getTimezoneOffset() * 1000 * 60
          "
        />
      </label>

      <label class="dl__group ghost-label">
        <h2 class="dl__topic">Til</h2>
        <input
          class="dl__description ghost-input"
          type="datetime-local"
          v-on:input="
            eventForm.toDate = new Date(
              $event.target.valueAsNumber + eventForm.toDate.getTimezoneOffset() * 1000 * 60,
            )
          "
          :valueAsNumber="
            eventForm.toDate.getTime() - eventForm.toDate.getTimezoneOffset() * 1000 * 60
          "
        />
      </label>

      <label class="dl__group dl__group--large ghost-label">
        <h2 class="dl__topic">Avtalekommentar</h2>
        <textarea
          class="dl__description ghost-input"
          v-model="eventForm.comment"
          placeholder="Ingen kommentar"
        ></textarea>
      </label>
    </div>

    <df-button type="submit" primary elevate :disabled="saving">
      Opprett
      <template v-slot:icon v-if="saving">
        <df-loading no-float />
      </template>
    </df-button>
  </form>
</template>

<style scoped>
.ghost-label:focus-within {
  box-shadow: inset 0 0 0 1px black;
}

.ghost-label .ghost-input {
  outline: none;
}

.ghost-input {
  appearance: none;
  display: block;
  width: 100%;
  text-align: left;
  background-color: transparent;
  border: none;
  padding: 0;
  resize: none;
}

input[type='datetime-local'] {
  @media (max-width: 1000px) {
    font-size: 0.85rem;
  }
}
</style>
